<script>
import {useProductStore} from '@js/store/ProductStore.js'
import IconHeart from '@js/components/Icons/IconHeart.vue'
import IconInfo from '@js/components/Icons/IconInfo.vue'
import NumberFormatComponent from '@js/components/Formatter/NumberFormatComponent.vue'
import {useLikeStore} from "@js/store/LikeStore.js";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import IconShare from "@js/components/Icons/IconShare.vue";
import {computed, onMounted, ref} from "vue";
import {useCartStore} from "@js/store/CartStore.js";
import {Link} from "@inertiajs/vue3";
import {useCallbackStore} from "@js/store/CallbackStore.js";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useCompareStore} from "@js/store/CompareStore.js";
import {numberCommaFormat} from '@js/helpers.ts';
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import CartQuantityStepper from "@js/components/Sections/Cart/Components/CartQuantityStepper.vue";

export default {
    name: 'ProductPricingBlock',
    components: {
        CartQuantityStepper,
        IconCheckMark,
        IconCartPlus,
        IconScales,
        Link,
        IconShare,
        IconHeartFilled,
        IconHeart,
        IconInfo,
        NumberFormatComponent
    },
    setup() {
        const isClient = ref(false);

        const likeStore = useLikeStore()
        const compareStore = useCompareStore()

        const cartStore = useCartStore()
        const callbackStore = useCallbackStore()


        const productStore = useProductStore();

        onMounted(() => {
            cartStore.loadCart()
            // Регистрируем продукт
            cartStore.registerCatalogProduct(productStore.product);
            isClient.value = true
        })

        const qtyProxy = computed({
            get: () => cartStore.uiOf(productStore.product.id).qty,
            set: (val) => cartStore.setQtyFromInput(productStore.product.id, val),
        });

        const hasAnyPrice = computed(() => !!productStore.product.price);

        const canBuy = computed(
            () => productStore.product.available && cartStore.priceActive(productStore.product.id) > 0
        );

        return {
            productStore,
            likeStore,
            compareStore,
            cartStore,
            callbackStore,
            isClient,
            numberCommaFormat,
            qtyProxy,
            hasAnyPrice,
            canBuy
        }
    },
}
</script>

<template>
    <div class="flex flex-col gap-3">
        <div class="bg-grayBg rounded-2xl p-4 flex flex-col md:flex-row gap-5">
            <div class="w-full flex flex-col gap-1">
                <div class="flex items-center justify-between mb-2">
                    <div class=" text-white text-xs font-medium px-2 py-1 rounded"
                         :class="[productStore.product.available ? 'bg-emerald-600': 'bg-red-500']">
                        {{ productStore.product.available ? 'Товар в наличии' : 'Ожидаем поставку' }}
                    </div>
                    <div class="flex items-center space-x-3">
                        <IconScales @click.prevent="compareStore.toggleCompare(productStore.product.id)"
                                    :class="[compareStore.isCompared(productStore.product.id) ? 'text-blue-500 md:hover:text-gray-600' : 'text-gray-600 md:hover:text-blue-500']"
                                    class="w-5 h-5 cursor-pointer transition"/>

                        <IconHeart v-if="!likeStore.isLiked(productStore.product.id)"
                                   @click.prevent="likeStore.toggleLike(productStore.product.id)"
                                   class="w-5 h-5 text-gray-600 md:hover:text-red-600 cursor-pointer transition"/>
                        <IconHeartFilled v-else @click.prevent="likeStore.toggleLike(productStore.product.id)"
                                         class="w-5 h-5 text-red-600 md:hover:text-gray-600 cursor-pointer transition"/>

                        <IconShare class="w-4 h-4 text-gray-600 md:hover:text-baseColor cursor-pointer transition"/>
                    </div>
                </div>

                <div class="flex w-full flex-col items-stretch gap-3">
                    <div v-if="hasAnyPrice" class="flex gap-10">
                        <div>
                            <div class="text-xs font-medium">
                                Цена за {{ productStore.product?.price_for_type?.vinitelniy_title ?? 'штуку' }}
                            </div>
                            <div>
                                <NumberFormatComponent :number="productStore.product.price"
                                                       append_text=" ₽" class="text-2xl font-semibold"
                                :decimal="0"/>
                            </div>
                        </div>
                    </div>
                    <div v-else class="font-medium leading-tight">
                        Цена договорная
                    </div>

                    <!-- Количество -->
                    <template v-if="canBuy">
                        <div class="flex justify-between gap-5">
                            <CartQuantityStepper v-model="qtyProxy"
                                                 :min="cartStore.minQty(productStore.product.id)"
                                                 :step="cartStore.step(productStore.product.id)"
                                                 :disabled="!canBuy"
                                                 size="sm"
                                                 width-class="w-32"
                                                 @inc="cartStore.incQty(productStore.product.id)"
                                                 @dec="cartStore.decQty(productStore.product.id)"/>
                        </div>
                    </template>

                    <!-- Итоги (сумма/вес) -->
                    <div class="flex items-center gap-10">
                        <div v-if="hasAnyPrice">
                            <div class="text-xs font-medium">
                                На сумму
                            </div>
                            <div class="text-sm font-semibold text-gray-900">
                                <NumberFormatComponent :number="cartStore.totalPrice(productStore.product.id)"
                                                       append_text=" ₽"/>
                            </div>
                        </div>
                        <div v-if="cartStore.weightKg(productStore.product.id)">
                            <div class="text-xs font-medium">
                                Вес
                            </div>
                            <div class="text-sm text-gray-900">
                                <NumberFormatComponent :number="cartStore.weightKg(productStore.product.id)"
                                                       append_text=" т"
                                                       prepend_text="≈ "/>
                            </div>
                        </div>
                        <div v-if="cartStore.areaSqM(productStore.product.id)">
                            <div class="text-xs font-medium">
                                Площадь
                            </div>
                            <div class="text-sm text-gray-900">
                                <NumberFormatComponent :number="cartStore.areaSqM(productStore.product.id)"
                                                       append_text=" кв.м"
                                                       prepend_text="≈ "/>
                            </div>
                        </div>

                    </div>

                    <!-- CTA -->
                    <template v-if="canBuy && isClient">
                        <template v-if="cartStore.isInCartExact(productStore.product.id)">
                            <Link :href="route('cart')"
                                  class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-base font-semibold text-white transition hover:bg-emerald-500">
                                <IconCheckMark class="w-5 h-5"/>
                                Уже в корзине
                            </Link>
                        </template>
                        <template v-else>
                            <button
                                class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-base font-semibold text-black transition md:hover:bg-emerald-500 md:hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="!canBuy"
                                @click="cartStore.addToCartFromCard(productStore.product.id)">
                                <IconCartPlus class="h-5 w-5"/>
                                В корзину
                            </button>
                        </template>

                        <button @click.prevent="productStore.showFastBuyPopup(productStore.product.id)"
                                class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-black/15 border-black/15 text-base font-semibold text-black transition hover:bg-baseColor/80 disabled:cursor-not-allowed disabled:opacity-50">
                            Купить сейчас
                        </button>
                    </template>
                    <template v-else>
                        <button @click.prevent="productStore.showBaseCallbackPopup"
                                class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                            Узнать цену
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <div class="bg-grayBg rounded-2xl p-4">
            <div class="w-full">
                <h3 class="text-lg font-semibold mb-2">Нашли дешевле?</h3>
                <div class="flex items-center text-gray-700">
          <span class="text-[13px] font-medium">
            Пришлите нам ссылку на этот товар в другом магазине, и в течение 24-х часов вы получите письмо на указанный вами E-mail с персональным предложением.
          </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
/* Ваши стили */
</style>
